import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { fetchEmployees } from '../../../store/slices/authSlice';
import ArchivedUserDetailModal from './components/ArchivedUserDetailModal';
import './ArchivedUsers.scss';

const ArchivedUsers = () => {
  const dispatch = useDispatch();
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'archived', direction: 'descending' });
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchArchivedUsers();
  }, []);

  const fetchArchivedUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const archivedUsersRef = collection(db, 'archivedUsers');
      const snapshot = await getDocs(archivedUsersRef);
      
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setArchivedUsers(users);
    } catch (err) {
      console.error('Error fetching archived users:', err);
      setError('Failed to fetch archived users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
  };

  const handleRejoinSuccess = async (newEmail) => {
    // Refresh the archived users list
    await fetchArchivedUsers();
    // Refresh the active employees list in Redux
    dispatch(fetchEmployees());
    // Optionally show a success message
    console.log('User rejoined successfully with email:', newEmail);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredUsers = archivedUsers.filter(user => {
    return (
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.empID && user.empID.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.designation && user.designation.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.organogram && user.organogram.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Handle timestamps
    if (aValue && (aValue.toDate || aValue instanceof Date)) {
      aValue = aValue.toDate ? aValue.toDate().getTime() : aValue.getTime();
    }
    if (bValue && (bValue.toDate || bValue instanceof Date)) {
      bValue = bValue.toDate ? bValue.toDate().getTime() : bValue.getTime();
    }
    
    // Handle strings
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    // Handle null/undefined
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;
    
    if (aValue < bValue) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? '↑' : '↓';
  };

  return (
    <div className="archived-users-container">
      <div className="page-header">
        <h1>Archived Users</h1>
        <p>View all terminated employees</p>
      </div>
      
      <div className="users-controls">
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Search archived users..." 
            value={searchTerm} 
            onChange={handleSearch}
          />
        </div>
        <button className="refresh-btn" onClick={fetchArchivedUsers}>
          Refresh
        </button>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading archived users...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchArchivedUsers}>Try Again</button>
        </div>
      ) : (
        <div className="users-table-container">
          {sortedUsers.length === 0 ? (
            <div className="no-users">
              <p>{searchTerm ? 'No archived users match your search criteria.' : 'No archived users found.'}</p>
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('empID')}>
                    Employee ID {getSortIcon('empID')}
                  </th>
                  <th onClick={() => handleSort('name')}>
                    Name {getSortIcon('name')}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email {getSortIcon('email')}
                  </th>
                  <th onClick={() => handleSort('designation')}>
                    Designation {getSortIcon('designation')}
                  </th>
                  <th onClick={() => handleSort('organogram')}>
                    Department {getSortIcon('organogram')}
                  </th>
                  <th onClick={() => handleSort('archived')}>
                    Archived Date {getSortIcon('archived')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    onClick={() => handleRowClick(user)}
                    className="user-row"
                  >
                    <td>{user.empID || 'N/A'}</td>
                    <td>
                      <div className="user-name">
                        {user.img && (
                          <img 
                            src={user.img} 
                            alt={user.name} 
                            className="user-avatar"
                          />
                        )}
                        <span>{user.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td>{user.email || 'N/A'}</td>
                    <td>{user.designation || 'N/A'}</td>
                    <td>{user.organogram || 'N/A'}</td>
                    <td>{formatDate(user.archived)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      
      {selectedUser && (
        <ArchivedUserDetailModal 
          user={selectedUser}
          onClose={handleCloseModal}
          onRejoinSuccess={handleRejoinSuccess}
        />
      )}
    </div>
  );
};

export default ArchivedUsers;
